import { useState, useEffect } from 'react';
import { FiX, FiTrendingUp, FiDollarSign } from 'react-icons/fi';
import { api } from '../../services/api';
import { useI18n } from '../../hooks/useI18n';
import { useAuth } from '../../contexts/AuthContext';

export function TradeModal({ open, onClose, market, pushToast, onSuccess }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const side = 'BUY';
  const [amount, setAmount] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [orderbook, setOrderbook] = useState(null);
  const [authChecking, setAuthChecking] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const [authProcessing, setAuthProcessing] = useState(false);

  // 选择的outcome (teamA 或 teamB)
  const [selectedOutcome, setSelectedOutcome] = useState('A');

  useEffect(() => {
    if (open && market) {
      loadBalance();
      loadOrderbook();
      checkAuthorization();
    } else if (!open) {
      setAuthRequired(false);
      setAuthProcessing(false);
      setAuthChecking(false);
      setStopLoss('');
      setTakeProfit('');
    }
  }, [open, market]);

  const checkAuthorization = async () => {
    setAuthChecking(true);
    try {
      const res = await api.getTradingAuthorizationStatus();
      if (res.code === 'SUCCESS') {
        setAuthRequired(!res.data?.authorized);
      }
    } catch (e) {
      console.error('检查授权状态失败', e);
      setAuthRequired(false);
    } finally {
      setAuthChecking(false);
    }
  };

  const loadBalance = async () => {
    try {
      const walletsRes = await api.getWallets();
      if (walletsRes.code === 'SUCCESS' && walletsRes.data?.length > 0) {
        const tradingWallet = walletsRes.data.find((w) => w.is_trading_wallet) || walletsRes.data[0];
        const addr = tradingWallet.safe_address || tradingWallet.wallet_address;
        const balanceRes = await api.getWalletBalance(addr);
        if (balanceRes.code === 'SUCCESS') {
          const usdc = (balanceRes.data?.usdc || 0) + (balanceRes.data?.usdc_native || 0);
          setBalance(usdc);
        }
      }
    } catch (e) {
      console.error('加载余额失败', e);
    }
  };

  const loadOrderbook = async () => {
    if (!market?.tokenIdA) return;
    try {
      const tokenId = selectedOutcome === 'A' ? market.tokenIdA : market.tokenIdB;
      const res = await api.getOrderbook(tokenId);
      if (res.code === 'SUCCESS') {
        setOrderbook(res.data);
      }
    } catch (e) {
      console.error('加载订单簿失败', e);
    }
  };

  useEffect(() => {
    if (open && market) {
      loadOrderbook();
    }
  }, [selectedOutcome]);

  const handleAuthorize = async () => {
    setAuthProcessing(true);
    try {
      const res = await api.authorizeTrading();
      if (res.code === 'SUCCESS') {
        setAuthRequired(false);
        pushToast?.(t('trading.authorizationSuccess'), 'success');
      } else {
        pushToast?.(res.msg || t('trading.authorizationFailed'), 'error');
      }
    } catch (e) {
      pushToast?.(e.message || t('trading.authorizationFailed'), 'error');
    }
    setAuthProcessing(false);
  };

  const handleTrade = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      pushToast?.(t('trading.enterAmount'), 'warning');
      return;
    }

    if (balance !== null && parseFloat(amount) > balance) {
      pushToast?.(t('trading.insufficientBalance'), 'error');
      return;
    }

    const tokenId = selectedOutcome === 'A' ? market.tokenIdA : market.tokenIdB;
    if (!tokenId) {
      pushToast?.(t('trading.tokenNotAvailable') || '该市场暂不支持交易', 'error');
      return;
    }

    if (authRequired) {
      pushToast?.(t('trading.authorizationRequired'), 'warning');
      return;
    }

    const isPro = user?.tier_name === 'pro';
    const stopLossValue = stopLoss ? parseFloat(stopLoss) : null;
    const takeProfitValue = takeProfit ? parseFloat(takeProfit) : null;

    if (!isPro && (stopLoss !== '' || takeProfit !== '')) {
      window.dispatchEvent(
        new CustomEvent('membership-required', { detail: { currentTier: user?.tier_name || 'free' } })
      );
      pushToast?.(t('trading.proOnly'), 'warning');
      return;
    }

    const currentPriceValue = currentPrice || 0;
    if (stopLossValue !== null) {
      if (Number.isNaN(stopLossValue) || stopLossValue <= 0 || stopLossValue >= 1) {
        pushToast?.(t('trading.invalidStopLoss'), 'warning');
        return;
      }
      if (currentPriceValue && stopLossValue >= currentPriceValue) {
        pushToast?.(t('trading.stopLossBelowCurrent'), 'warning');
        return;
      }
    }
    if (takeProfitValue !== null) {
      if (Number.isNaN(takeProfitValue) || takeProfitValue <= 0 || takeProfitValue >= 1) {
        pushToast?.(t('trading.invalidTakeProfit'), 'warning');
        return;
      }
      if (currentPriceValue && takeProfitValue <= currentPriceValue) {
        pushToast?.(t('trading.takeProfitAboveCurrent'), 'warning');
        return;
      }
    }
    if (stopLossValue !== null && takeProfitValue !== null && stopLossValue >= takeProfitValue) {
      pushToast?.(t('trading.invalidStopLossRange'), 'warning');
      return;
    }

    setLoading(true);
    try {
      const res = await api.placeOrder(tokenId, side, parseFloat(amount), null, {
        stop_loss_price: stopLossValue,
        take_profit_price: takeProfitValue,
        market_title: `${market.teamA} vs ${market.teamB}`,
        outcome: teamName,
        condition_id: market.conditionId,
      });

      if (res.code === 'AUTH_REQUIRED') {
        setAuthRequired(true);
        pushToast?.(t('trading.authorizationRequired'), 'warning');
        setLoading(false);
        return;
      }

      if (res.code === 'SUCCESS') {
        const data = res.data;
        const isSuccess = data.status === 'success' || data.status === 'partial';
        const isSubmitted = data.status === 'submitted';
        if (isSuccess) {
          pushToast?.(
            t('trading.orderSuccess', {
              filled: data.filled_size?.toFixed(2),
              price: data.avg_price?.toFixed(4),
            }),
            'success'
          );
          onSuccess?.();
          onClose();
        } else if (isSubmitted) {
          pushToast?.(t('trading.orderSubmitted'), 'info');
          onSuccess?.();
          onClose();
        } else {
          pushToast?.(data.message || t('trading.orderFailed'), 'error');
        }
      } else {
        pushToast?.(res.msg || t('trading.orderFailed'), 'error');
      }
    } catch (e) {
      if (e?.isMembershipRequired) {
        window.dispatchEvent(
          new CustomEvent('membership-required', { detail: { currentTier: e.currentTier || user?.tier_name || 'free' } })
        );
        pushToast?.(t('trading.proOnly'), 'warning');
      } else {
        pushToast?.(e.message || t('trading.requestFailed'), 'error');
      }
    }
    setLoading(false);
  };

  if (!open || !market) return null;

  const currentPrice = selectedOutcome === 'A' ? market.priceA : market.priceB;
  const teamName = selectedOutcome === 'A' ? market.teamA : market.teamB;
  const estimatedShares = amount && currentPrice ? (parseFloat(amount) / currentPrice).toFixed(2) : '0';
  const stopLossValuePreview = stopLoss !== '' ? parseFloat(stopLoss) : null;
  const takeProfitValuePreview = takeProfit !== '' ? parseFloat(takeProfit) : null;

  const getValidationError = () => {
    if (stopLossValuePreview !== null) {
      if (Number.isNaN(stopLossValuePreview) || stopLossValuePreview <= 0 || stopLossValuePreview >= 1) {
        return t('trading.invalidStopLoss');
      }
      if (currentPrice && stopLossValuePreview >= currentPrice) {
        return t('trading.stopLossBelowCurrent');
      }
    }
    if (takeProfitValuePreview !== null) {
      if (Number.isNaN(takeProfitValuePreview) || takeProfitValuePreview <= 0 || takeProfitValuePreview >= 1) {
        return t('trading.invalidTakeProfit');
      }
      if (currentPrice && takeProfitValuePreview <= currentPrice) {
        return t('trading.takeProfitAboveCurrent');
      }
    }
    if (
      stopLossValuePreview !== null &&
      takeProfitValuePreview !== null &&
      stopLossValuePreview >= takeProfitValuePreview
    ) {
      return t('trading.invalidStopLossRange');
    }
    return null;
  };

  const validationError = getValidationError();
  const tradeDisabled = loading || !amount || authChecking || authRequired || !!validationError;
  const isPro = user?.tier_name === 'pro';

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-surface-highlight rounded-2xl border border-glass-border shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-glass-border">
          <div>
            <h3 className="text-lg font-bold text-white">{t('trading.trade')}</h3>
            <p className="text-sm text-gray-400 truncate max-w-[280px]">{market.teamA} vs {market.teamB}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-surface-highlight transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Outcome Selection */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-400">{t('trading.selectOutcome')}</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setSelectedOutcome('A')}
                className={`p-3 rounded-xl text-sm font-medium transition-all border ${
                  selectedOutcome === 'A'
                    ? 'bg-positive/20 border-positive/50 text-positive'
                    : 'bg-surface border-glass-border text-gray-400 hover:text-white hover:border-glass-border/80'
                }`}
              >
                <div className="font-bold truncate">{market.teamA}</div>
                <div className="text-xs mt-1 opacity-80">{(market.priceA * 100).toFixed(1)}%</div>
              </button>
              <button
                onClick={() => setSelectedOutcome('B')}
                className={`p-3 rounded-xl text-sm font-medium transition-all border ${
                  selectedOutcome === 'B'
                    ? 'bg-negative/20 border-negative/50 text-negative'
                    : 'bg-surface border-glass-border text-gray-400 hover:text-white hover:border-glass-border/80'
                }`}
              >
                <div className="font-bold truncate">{market.teamB}</div>
                <div className="text-xs mt-1 opacity-80">{(market.priceB * 100).toFixed(1)}%</div>
              </button>
            </div>
          </div>

          {/* Amount Input */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <label className="text-xs font-medium text-gray-400">
                {t('trading.amountUsdc')}
              </label>
              {balance !== null && (
                <span className="text-xs text-gray-500">
                  {t('wallet.balance')}: ${balance.toFixed(2)}
                </span>
              )}
            </div>
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full bg-surface border border-glass-border rounded-xl pl-10 pr-16 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/50 transition-all"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <FiDollarSign size={18} />
              </div>
              {balance !== null && (
                <button
                  onClick={() => setAmount(balance.toFixed(2))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-accent hover:text-accent-soft font-medium px-2 py-1 rounded hover:bg-accent/10 transition-colors"
                >
                  {t('wallet.max')}
                </button>
              )}
            </div>
          </div>

          {/* Stop Loss / Take Profit */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-400">{t('trading.autoExit')}</label>
              {!isPro && (
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('membership-required', { detail: { currentTier: user?.tier_name || 'free' } }))}
                  className="text-[10px] uppercase tracking-wider text-amber-300 bg-amber-400/10 border border-amber-400/30 px-2 py-0.5 rounded-full"
                >
                  {t('trading.proOnlyBadge')}
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[11px] text-gray-500">{t('trading.stopLossLabel')}</label>
                <input
                  type="number"
                  step="0.0001"
                  min="0.0001"
                  max={currentPrice ? Math.max(0, currentPrice - 0.0001) : 0.9999}
                  disabled={!isPro}
                  value={stopLoss}
                  onChange={(e) => setStopLoss(e.target.value)}
                  placeholder="0.0000"
                  className={`w-full bg-surface border border-glass-border rounded-xl px-3 py-2 text-white placeholder-gray-600 focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/50 transition-all text-sm ${
                    !isPro ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                />
                <div className="text-[10px] text-gray-500">{t('trading.stopLossHint')}</div>
              </div>
              <div className="space-y-1">
                <label className="text-[11px] text-gray-500">{t('trading.takeProfitLabel')}</label>
                <input
                  type="number"
                  step="0.0001"
                  min={currentPrice ? Math.min(0.9999, currentPrice + 0.0001) : 0.0001}
                  max="0.9999"
                  disabled={!isPro}
                  value={takeProfit}
                  onChange={(e) => setTakeProfit(e.target.value)}
                  placeholder="0.0000"
                  className={`w-full bg-surface border border-glass-border rounded-xl px-3 py-2 text-white placeholder-gray-600 focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/50 transition-all text-sm ${
                    !isPro ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                />
                <div className="text-[10px] text-gray-500">{t('trading.takeProfitHint')}</div>
              </div>
            </div>
            {validationError && (
              <div className="text-xs text-negative">{validationError}</div>
            )}
          </div>

          {/* Order Preview */}
          <div className="p-4 bg-surface rounded-xl border border-glass-border space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('trading.outcome')}</span>
              <span className="text-white font-medium">{teamName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('trading.currentPrice')}</span>
              <span className="text-white font-mono">${currentPrice?.toFixed(4)}</span>
            </div>
            {amount && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('trading.estimatedShares')}</span>
                <span className="text-positive font-mono">~{estimatedShares}</span>
              </div>
            )}
            <div className="flex justify-between text-sm pt-2 border-t border-glass-border/50">
              <span className="text-gray-500">{t('trading.potentialPayout')}</span>
              <span className="text-white font-bold">
                ${estimatedShares}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-glass-border flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-gray-400 hover:text-white hover:bg-surface-highlight transition-colors text-sm font-medium"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleTrade}
            disabled={tradeDisabled}
            className="px-6 py-2 rounded-xl text-white text-sm font-medium shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 bg-positive hover:bg-positive/80 shadow-positive/20"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <FiTrendingUp />
            )}
            {t('trading.confirmBuy')}
          </button>
        </div>
      </div>
      {authRequired && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-surface-highlight rounded-2xl border border-glass-border shadow-xl p-6 space-y-4">
            <h4 className="text-lg font-bold text-white">{t('trading.authorizationTitle')}</h4>
            <p className="text-sm text-gray-400 leading-relaxed">{t('trading.authorizationDescription')}</p>
            {authProcessing && (
              <div className="text-xs text-gray-500">{t('trading.authorizationPending')}</div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl text-gray-400 hover:text-white hover:bg-surface-highlight transition-colors text-sm font-medium"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleAuthorize}
                disabled={authProcessing}
                className="px-5 py-2 rounded-xl text-white text-sm font-medium shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 bg-accent hover:bg-accent/80 shadow-accent/20"
              >
                {authProcessing ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  t('trading.authorizationAction')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
